import { Show, EditButton } from "@refinedev/antd";
import {
  useShow,
  useUpdate,
  useList,
  useApiUrl,
  useCustomMutation,
  useNavigation,
} from "@refinedev/core";
import {
  Descriptions,
  Tag,
  Typography,
  Space,
  Card,
  Row,
  Col,
  Button,
  Popconfirm,
  Avatar,
  List as AntdList,
  Empty,
  Progress,
  Tooltip,
} from "antd";
import {
  StopOutlined,
  CheckCircleOutlined,
  LoginOutlined,
  CreditCardOutlined,
  ApartmentOutlined,
  MailOutlined,
  CrownOutlined,
} from "@ant-design/icons";
import { useMemo } from "react";
import type { User, Brain, PlanRecord } from "@/lib/types";
import { formatDate, brl } from "@/lib/format";
import { StatCard } from "@/components/StatCard";
import { FREE_LIMIT, PREMIUM_LIMIT } from "@/lib/constants";

const { Title, Text, Paragraph } = Typography;

type AdminUser = User & { banned?: boolean; nome?: string | null };

/**
 * /admin/users/:id — detalhe da conta + dados relacionados.
 *
 * Como obtém os dados (MOCK MODE): `useShow` traz o usuário (getOne contra
 * FIXTURES.users). `useList` em brains/plans (filtrados por user_id, mode:off)
 * monta as seções de Brains do usuário e Assinatura — mesma fonte de
 * GET /admin/users/:id, /admin/brains?userId=, /admin/billing?userId= na API real.
 *
 * Ações administrativas (auditáveis): Banir/Reativar (useUpdate), Impersonate
 * (POST /admin/users/:id/impersonate) e Editar plano/quota (rota de edit).
 */
export const UserShow: React.FC = () => {
  const { queryResult } = useShow<AdminUser>();
  const { data, isLoading } = queryResult;
  const u = data?.data;
  const { show } = useNavigation();

  // Brains do usuário (moderação cruzada).
  const { data: brainsData, isLoading: brainsLoading } = useList<Brain>({
    resource: "brains",
    pagination: { mode: "off" },
    filters: u ? [{ field: "user_id", operator: "eq", value: u.id }] : [],
    queryOptions: { enabled: !!u?.id },
  });
  const brains = useMemo(() => brainsData?.data ?? [], [brainsData]);

  // Assinatura (billing) do usuário, se houver.
  const { data: planData } = useList<PlanRecord>({
    resource: "plans",
    pagination: { mode: "off" },
    filters: u ? [{ field: "user_id", operator: "eq", value: u.id }] : [],
    queryOptions: { enabled: !!u?.id },
  });
  const subscription = planData?.data?.[0];

  // ── Ações ──────────────────────────────────────────────────────────────────
  const { mutate: updateUser } = useUpdate();
  const setBanned = (banned: boolean) => {
    if (!u) return;
    updateUser({
      resource: "users",
      id: u.id,
      values: { banned },
      mutationMode: "optimistic",
      successNotification: () => ({
        type: "success",
        message: banned ? "Usuário banido" : "Usuário reativado",
        description: u.email,
      }),
    });
  };

  const apiUrl = useApiUrl();
  const { mutate: impersonate, isLoading: impersonating } = useCustomMutation();
  const doImpersonate = () => {
    if (!u) return;
    impersonate({
      url: `${apiUrl}/users/${u.id}/impersonate`,
      method: "post",
      values: {},
      successNotification: () => ({
        type: "success",
        message: "Sessão de impersonação criada",
        description: `Agindo como ${u.email}. Ação registrada no audit log.`,
      }),
      errorNotification: () => ({
        type: "error",
        message: "Não foi possível impersonar",
        description: "Verifique permissões e o audit log.",
      }),
    });
  };

  const isAdmin = u?.role === "admin";
  const limit = u?.plano === "premium" ? PREMIUM_LIMIT : FREE_LIMIT;
  const used = (u?.registros_mes ?? 0) + (u?.registros_extras ?? 0);
  const pct = Math.min(100, Math.round((used / limit) * 100));

  return (
    <Show
      isLoading={isLoading}
      title="Detalhe da conta"
      headerButtons={({ defaultButtons }) => (
        <Space wrap>
          {defaultButtons}
          <EditButton>Editar plano/quota</EditButton>
          {u?.banned ? (
            <Popconfirm
              title="Reativar conta?"
              okText="Reativar"
              cancelText="Cancelar"
              onConfirm={() => setBanned(false)}
            >
              <Button icon={<CheckCircleOutlined />} style={{ color: "#10B981" }}>
                Reativar
              </Button>
            </Popconfirm>
          ) : (
            <Popconfirm
              title="Banir esta conta?"
              description="Bloqueia o acesso. Ação auditada."
              okText="Banir"
              okButtonProps={{ danger: true }}
              cancelText="Cancelar"
              onConfirm={() => setBanned(true)}
              disabled={isAdmin}
            >
              <Button danger icon={<StopOutlined />} disabled={isAdmin}>
                Banir
              </Button>
            </Popconfirm>
          )}
          <Tooltip
            title={isAdmin ? "Não é possível impersonar outro admin" : undefined}
          >
            <Button
              type="primary"
              icon={<LoginOutlined />}
              loading={impersonating}
              disabled={isAdmin || u?.banned}
              onClick={doImpersonate}
            >
              Impersonar
            </Button>
          </Tooltip>
        </Space>
      )}
    >
      <Space direction="vertical" size={16} style={{ width: "100%" }}>
        {/* Cabeçalho identitário */}
        <Card className="mel-glass" bordered={false}>
          <Space size={16} align="start" wrap>
            <Avatar
              size={56}
              style={{
                background: "linear-gradient(135deg,#06B6D4 0%,#2DD4BF 100%)",
                fontWeight: 700,
              }}
            >
              {(u?.nome ?? u?.email ?? "?").charAt(0).toUpperCase()}
            </Avatar>
            <Space direction="vertical" size={2}>
              <Space size={8} wrap>
                <Title level={4} style={{ margin: 0 }}>
                  {u?.nome ?? u?.email}
                </Title>
                <Tag color={u?.plano === "premium" ? "gold" : "default"}>
                  {u?.plano}
                </Tag>
                <Tag color={isAdmin ? "geekblue" : "default"}>{u?.role}</Tag>
                {u?.banned && <Tag color="red">banido</Tag>}
              </Space>
              <Text type="secondary">
                <MailOutlined /> {u?.email}{" "}
                {u?.email_verified ? (
                  <Tag color="green" style={{ marginLeft: 4 }}>
                    verificado
                  </Tag>
                ) : (
                  <Tag color="orange" style={{ marginLeft: 4 }}>
                    não verificado
                  </Tag>
                )}
              </Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {u?.id} · entrou em {formatDate(u?.created_at)}
              </Text>
            </Space>
          </Space>
        </Card>

        {/* KPIs rápidos */}
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={8}>
            <StatCard
              title="Registros no mês"
              value={u?.registros_mes ?? 0}
              prefix={<CrownOutlined />}
              hint={`${used} de ${limit} (${pct}%) incl. extras`}
            />
          </Col>
          <Col xs={24} sm={8}>
            <StatCard
              title="MRR do usuário"
              value={brl.format((subscription?.mrr_cents ?? 0) / 100)}
              prefix={<CreditCardOutlined />}
              hint={subscription ? subscription.status : "sem assinatura"}
            />
          </Col>
          <Col xs={24} sm={8}>
            <StatCard
              title="Brains"
              value={brains.length}
              prefix={<ApartmentOutlined />}
              hint="contextos do usuário"
            />
          </Col>
        </Row>

        {/* Uso/quota */}
        <Card className="mel-glass" bordered={false} title="Uso da quota (mês)">
          <Progress
            percent={pct}
            status={pct >= 100 ? "exception" : "active"}
            strokeColor={pct >= 100 ? undefined : { from: "#06B6D4", to: "#2DD4BF" }}
          />
          <Text type="secondary" style={{ fontSize: 12 }}>
            {u?.registros_mes ?? 0} registros + {u?.registros_extras ?? 0} extras ·
            limite do plano {u?.plano}: {limit}
          </Text>
        </Card>

        <Row gutter={[16, 16]}>
          <Col xs={24} lg={12}>
            <Title level={5}>Plano / Freemium</Title>
            <Descriptions bordered column={1} size="small">
              <Descriptions.Item label="Plano">
                <Tag color={u?.plano === "premium" ? "gold" : "default"}>
                  {u?.plano}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Cobrança">
                {u?.tipo_cobranca}
              </Descriptions.Item>
              <Descriptions.Item label="Stripe customer">
                {u?.stripe_customer_id ?? "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Renovação">
                {formatDate(u?.data_renovacao)}
              </Descriptions.Item>
              <Descriptions.Item label="Assinatura">
                {subscription ? (
                  <Button
                    type="link"
                    size="small"
                    style={{ padding: 0 }}
                    onClick={() => show("plans", subscription.id)}
                  >
                    Ver billing ({subscription.id})
                  </Button>
                ) : (
                  "—"
                )}
              </Descriptions.Item>
            </Descriptions>
          </Col>
          <Col xs={24} lg={12}>
            <Title level={5}>Preferências</Title>
            <Descriptions bordered column={1} size="small">
              <Descriptions.Item label="E-mail semanal">
                {u?.weekly_email ? "Sim" : "Não"}
              </Descriptions.Item>
              <Descriptions.Item label="Onboarding">
                {u?.onboarding_done ? "Concluído" : "Pendente"}
              </Descriptions.Item>
              <Descriptions.Item label="Provider">
                {u?.provider}
              </Descriptions.Item>
              <Descriptions.Item label="Contexto pessoal">
                <Paragraph style={{ margin: 0 }} ellipsis={{ rows: 2, expandable: true }}>
                  {u?.contexto_pessoal ?? "—"}
                </Paragraph>
              </Descriptions.Item>
            </Descriptions>
          </Col>
        </Row>

        {/* Brains do usuário */}
        <Card
          className="mel-glass"
          bordered={false}
          title={
            <Space>
              <ApartmentOutlined /> Brains do usuário
            </Space>
          }
        >
          {brains.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={brainsLoading ? "Carregando…" : "Nenhum brain"}
            />
          ) : (
            <AntdList
              dataSource={brains}
              renderItem={(b) => (
                <AntdList.Item
                  actions={[
                    <Button
                      key="open"
                      type="link"
                      size="small"
                      onClick={() => show("brains", b.id)}
                    >
                      Abrir
                    </Button>,
                  ]}
                >
                  <AntdList.Item.Meta
                    avatar={<Avatar>{b.emoji ?? "🧠"}</Avatar>}
                    title={
                      <Space>
                        {b.nome}
                        {b.is_default && <Tag color="cyan">default</Tag>}
                        {b.flagged && <Tag color="red">denunciado</Tag>}
                      </Space>
                    }
                    description={
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {b.members_count ?? 0} membro(s) · {b.entries_count ?? 0}{" "}
                        entrada(s) · criado em {formatDate(b.created_at)}
                      </Text>
                    }
                  />
                </AntdList.Item>
              )}
            />
          )}
        </Card>
      </Space>
    </Show>
  );
};
